import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  AlertTriangle,
  BarChart3,
  BrainCircuit,
  CalendarDays,
  DollarSign,
  Download,
  Filter,
  Gauge,
  Inbox,
  Loader2,
  PieChart,
  Target,
  TrendingUp,
  Users2,
} from 'lucide-react';
import { useTheme } from 'styled-components';
import { toast } from 'react-hot-toast';
import { useApi } from '../hooks/useApi';
import { api } from '../services/api';

const rotate = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.9;
  }
  50% {
    transform: scale(1.04);
    opacity: 1;
  }
`;

const subtleFloat = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
`;

const PageContainer = styled.div`
  padding: 48px 32px 64px;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 768px) {
    padding: 32px 20px 48px;
  }
`;

const PageHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HeaderTitle = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.01em;
`;

const HeaderSubtitle = styled.p`
  margin: 0;
  max-width: 640px;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;

const FiltersCard = styled.section`
  position: relative;
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  padding: ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[5]};
  }
`;

const FiltersHeader = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  z-index: 1;
`;

const FiltersTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${({ theme }) => theme.colors.textPrimary};

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const FiltersSubtitle = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const BetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.borders.radius.full};
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const FiltersGrid = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  z-index: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-transform: uppercase;
  letter-spacing: 0.04em;

  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const controlStyles = css`
  width: 100%;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 0.9375rem;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: border-color 220ms ease, box-shadow 220ms ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}10;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderLight};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
    opacity: 0.65;
  }
`;

const Input = styled.input`
  ${controlStyles}
`;

const Select = styled.select`
  ${controlStyles}
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 2px solid ${({ theme }) => theme.colors.border};
  accent-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease;

  &:hover {
    transform: scale(1.05);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px rgba(255, 59, 48, 0.25);
  }
`;

const FiltersDivider = styled.div`
  height: 1px;
  width: 100%;
  margin: 24px 0 20px;
  background: ${({ theme }) => theme.colors.border};
`;

const ButtonGroup = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  z-index: 1;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled.button<{
  $variant?: 'primary' | 'secondary' | 'ghost';
  $loading?: boolean;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[6]}`};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  background: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'secondary':
        return theme.colors.backgroundTertiary;
      case 'ghost':
        return 'transparent';
      default:
        return theme.colors.primary;
    }
  }};
  color: ${({ theme, $variant }) =>
    $variant === 'secondary' || $variant === 'ghost'
      ? theme.colors.textPrimary
      : theme.colors.textPrimary};
  border: ${({ theme, $variant }) =>
    $variant === 'ghost' ? `1px solid ${theme.colors.border}` : $variant === 'secondary' ? `1px solid ${theme.colors.border}` : 'none'};
  box-shadow: ${({ $variant, theme }) =>
    $variant === 'ghost' ? 'none' : theme.shadows.sm};

  &:hover {
    background: ${({ theme, $variant }) =>
      $variant === 'secondary'
        ? theme.colors.backgroundSecondary
        : $variant === 'ghost'
        ? theme.colors.backgroundTertiary
        : theme.colors.primaryHover};
    border-color: ${({ theme, $variant }) =>
      $variant === 'ghost' || $variant === 'secondary' ? theme.colors.borderLight : 'transparent'};
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${({ $loading }) =>
    $loading &&
    css`
      pointer-events: none;
      opacity: 0.8;
    `}

  svg {
    width: 18px;
    height: 18px;
  }
`;

const DownloadButton = styled(Button)`
  animation: ${pulse} 8s ease-in-out infinite;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: ${({ theme }) => theme.borders.radius.full};
  background: ${({ theme }) => theme.colors.border};
  overflow: hidden;
  margin-top: ${({ theme }) => theme.spacing[3]};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    width: ${({ $progress }) => `${$progress}%`};
    background: ${({ theme }) => theme.colors.primary};
    transition: width 180ms ease;
  }
`;

const StatsSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StatsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9375rem;

  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
`;

const StatCard = styled.div<{
  $gradient: string;
  $accent: string;
  $delay: number;
}>`
  position: relative;
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  padding: ${({ theme }) => theme.spacing[6]};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 3px solid ${({ $accent }) => $accent};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: all ${({ theme }) => theme.animations.duration.normal} ${({ theme }) => theme.animations.easing.easeInOut};
  opacity: 0;
  animation: ${fadeInUp} 480ms ease forwards;
  animation-delay: ${({ $delay }) => `${$delay}ms`};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.borderLight};
  }
`;

const StatIcon = styled.div<{ $accent: string }>`
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  display: grid;
  place-items: center;
  background: ${({ $accent }) => `${$accent}15`};
  color: ${({ $accent }) => $accent};
  margin-bottom: ${({ theme }) => theme.spacing[5]};

  svg {
    width: 22px;
    height: 22px;
  }
`;

const StatLabel = styled.span`
  display: block;
  font-size: 0.875rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 8px;
`;

const StatValue = styled.div<{ $tone?: string }>`
  font-size: clamp(2rem, 1.8vw + 1.4rem, 2.6rem);
  font-weight: 700;
  color: ${({ $tone, theme }) => $tone ?? theme.colors.textPrimary};
  margin-bottom: 12px;
  line-height: 1.1;
`;

const StatHint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => (theme.isLight ? 'rgba(34, 36, 40, 0.7)' : 'rgba(241, 245, 249, 0.72)')};
`;

const StatsSkeletonGrid = styled(StatsGrid)`
  margin-top: 12px;
`;

const SkeletonCard = styled.div`
  position: relative;
  border-radius: 20px;
  padding: 24px;
  background: ${({ theme }) =>
    theme.isLight ? 'rgba(226, 232, 240, 0.6)' : 'rgba(30, 41, 59, 0.35)'};
  overflow: hidden;
  min-height: 140px;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      120deg,
      transparent 0%,
      rgba(148, 163, 184, 0.25) 45%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 2.2s infinite;
  }
`;

const AnalysisSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SectionSubtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
`;

const PeriodBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  align-self: flex-start;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const SubSectionTitle = styled.h3`
  margin: 8px 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const AnalysisGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 18px;
`;

const AnalysisCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[5]}`};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const AnalysisLabel = styled.span`
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const AnalysisValue = styled.strong`
  font-size: 1.35rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const TopList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TopListItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const TopItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const TopItemMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DetailedSkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 18px;
`;

const SkeletonTile = styled.div`
  height: 120px;
  border-radius: 18px;
  background: ${({ theme }) =>
    theme.isLight
      ? 'linear-gradient(90deg, rgba(248, 250, 252, 0.85) 0%, rgba(229, 231, 235, 0.9) 50%, rgba(248, 250, 252, 0.85) 100%)'
      : 'linear-gradient(90deg, rgba(31, 41, 55, 0.78) 0%, rgba(55, 65, 81, 0.9) 50%, rgba(31, 41, 55, 0.78) 100%)'};
  background-size: 200% 100%;
  animation: ${shimmer} 1.8s ease infinite;
`;

const InsightsCard = styled.section`
  position: relative;
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  padding: ${({ theme }) => theme.spacing[7]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[6]};
  }
`;

const InsightsHeader = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 24px;
  z-index: 1;
`;

const InsightsTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const InsightsTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const InsightsIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  display: grid;
  place-items: center;
  background: ${({ theme }) => `${theme.colors.primary}15`};
  color: ${({ theme }) => theme.colors.primary};

  svg {
    width: 22px;
    height: 22px;
  }
`;

const InsightsTitle = styled.h3`
  margin: 0;
  font-size: clamp(1.35rem, 1vw + 1rem, 1.75rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const InsightsSubtitle = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 520px;
`;

const InsightsBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.borders.radius.full};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const InsightsContent = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
`;

const InsightsSkeletonGrid = styled(InsightsContent)`
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const InsightTile = styled.div`
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[5]}`};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const InsightHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const InsightList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InsightListItem = styled.li`
  position: relative;
  padding-left: 18px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.55;

  &::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 0.7em;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-50%);
  }
`;

const InsightKeyword = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => `${theme.spacing[10]} ${theme.spacing[6]}`};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
`;

const LoadingSpinner = styled(Loader2)`
  width: 28px;
  height: 28px;
  color: ${({ theme }) => theme.colors.primary};
  animation: ${rotate} 1s linear infinite;
`;

const ErrorMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[6]};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  border: 1px solid ${({ theme }) => theme.colors.error}40;
  background: ${({ theme }) =>
    theme.isLight ? `${theme.colors.error}10` : `${theme.colors.error}15`};
  color: ${({ theme }) => theme.colors.error};
`;

const ErrorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ErrorActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => `${theme.spacing[12]} ${theme.spacing[6]}`};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};

  svg {
    width: 36px;
    height: 36px;
    color: ${({ theme }) => theme.colors.textTertiary};
  }
`;

interface ReportFilters {
  tipoRelatorio: string;
  dataInicio: string;
  dataFim: string;
  incluirInsights: boolean;
}

interface ReportStats {
  totalLeads?: number;
  totalVendas?: number;
  valorTotal?: number;
  taxaConversao?: number;
  ticketMedio?: number;
  [key: string]: any;
}

interface ReportData {
  estatisticas: ReportStats;
  insights?: string;
  periodoInicio?: string;
  periodoFim?: string;
  vendas?: Array<Record<string, any>>;
  leads?: Array<Record<string, any>>;
  interacoes?: Array<Record<string, any>>;
}

type StatDefinition = {
  key: string;
  label: string;
  icon: React.ComponentType;
  value: number;
  formatter: (value: number) => string;
  gradient: string;
  accent: string;
  hint: string;
  tone?: string;
};

type InsightSection = {
  title: string;
  items: string[];
};

const INSIGHT_KEYWORDS = [
  'crescimento',
  'conversão',
  'receita',
  'vendas',
  'leads',
  'alerta',
  'meta',
  'oportunidade',
  'tendência',
  'risco',
  'performance',
  'faturamento',
  'prioridade',
];

const normalizeText = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const METRIC_LABELS: Record<string, string> = {
  totalLeads: 'Total de Leads',
  taxaConversao: 'Taxa de Conversão',
  totalInteracoes: 'Total de Interações',
  valorTotal: 'Valor Total',
  totalVendas: 'Total de Vendas',
  leadsPorStatus: 'Leads por Status',
  ticketMedio: 'Ticket Médio',
  valorPrevisto: 'Valor Previsto',
};

const replaceMetricLabels = (text: string) =>
  Object.entries(METRIC_LABELS).reduce((acc, [key, label]) => {
    const pattern = new RegExp(`\\b${key}\\b`, 'gi');
    return acc.replace(pattern, label);
  }, text);

const formatMetricLabel = (key: string) => {
  if (!key) return '';
  if (METRIC_LABELS[key]) return METRIC_LABELS[key];

  const withSpaces = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
    .toLowerCase();

  return withSpaces.replace(/\b\w/g, (char) => char.toUpperCase());
};

const Reports: React.FC = () => {
  const theme = useTheme();
  const { request } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadingState, setLoadingState] = useState<'idle' | 'summary' | 'generate' | 'download'>('idle');
  const [lastAction, setLastAction] = useState<'summary' | 'generate' | 'download' | null>(null);
  const [progress, setProgress] = useState(0);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [filters, setFilters] = useState<ReportFilters>({
    tipoRelatorio: 'completo',
    dataInicio: '',
    dataFim: '',
    incluirInsights: true,
  });

  const beginProgress = (target = 85) => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    setProgress(6);
    progressIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        const increment = Math.random() * 12 + 5;
        const next = Math.min(prev + increment, target);
        if (next >= target && progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        return next;
      });
    }, 260);
  };

  const completeProgress = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    setProgress(100);
    setTimeout(() => setProgress(0), 480);
  };

  const loadSummary = async () => {
    try {
      setLoading(true);
       setLoadingState('summary');
      const response = await request<{ data: { estatisticas: ReportStats } }>(
        '/api/v1/reports/stats/summary'
      );

      if (response?.data?.estatisticas) {
        setReportData({ estatisticas: response.data.estatisticas });
        setHasLoadedOnce(true);
      }
    } catch (err: any) {
      console.error('Erro ao carregar resumo:', err);
      setError('Erro ao carregar resumo de estatísticas');
    } finally {
      setLoading(false);
      setLoadingState('idle');
    }
  };

  useEffect(() => {
    loadSummary();

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const handleFilterChange = (field: keyof ReportFilters, value: string | boolean) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const generateReport = async () => {
    try {
      setLoading(true);
      setLoadingState('generate');
      setError(null);
      setLastAction('generate');
      beginProgress(92);

      const params = new URLSearchParams({
        tipoRelatorio: filters.tipoRelatorio,
        incluirInsights: String(filters.incluirInsights),
      });

      if (filters.dataInicio) params.append('dataInicio', filters.dataInicio);
      if (filters.dataFim) params.append('dataFim', filters.dataFim);

      const response = await request<{ data: ReportData }>(
        `/api/v1/reports/generate?${params.toString()}`
      );

      if (response?.data) {
        setReportData(response.data);
        setHasLoadedOnce(true);
      }
    } catch (err: any) {
      console.error('Erro ao gerar relatório:', err);
      setError(err.message || 'Erro ao gerar relatório');
    } finally {
      setLoading(false);
      setLoadingState('idle');
      completeProgress();
    }
  };

  const downloadPDF = async () => {
    try {
      setLoading(true);
      setLoadingState('download');
      setError(null);
      setLastAction('download');
      beginProgress(96);

      const params: Record<string, string> = {
        tipoRelatorio: filters.tipoRelatorio,
        incluirInsights: String(filters.incluirInsights),
      };

      if (filters.dataInicio) params.dataInicio = filters.dataInicio;
      if (filters.dataFim) params.dataFim = filters.dataFim;

      const response = await api.get('/api/v1/reports/download/pdf', {
        params,
        responseType: 'blob',
      });

      const filename = `relatorio_${filters.tipoRelatorio}_${new Date().toISOString().split('T')[0]}.pdf`;
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      toast.success('Relatório em PDF gerado com sucesso.');

    } catch (err: any) {
      console.error('Erro ao baixar PDF:', err);
      setError(err.message || 'Erro ao baixar PDF');
    } finally {
      setLoading(false);
      setLoadingState('idle');
      completeProgress();
    }
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatPercent = (value?: number) => {
    if (value === undefined || value === null) return '0,00%';
    return `${value.toFixed(2)}%`;
  };

  const statsConfig = useMemo(() => {
    const conversionValue = reportData?.estatisticas?.taxaConversao ?? 0;
    const conversionTone =
      conversionValue >= 25
        ? theme.colors.success
        : conversionValue >= 10
        ? '#facc15'
        : '#f87171';

    const config: StatDefinition[] = [
      {
        key: 'totalLeads',
        label: 'Total de Leads',
        icon: Users2,
        value: reportData?.estatisticas?.totalLeads ?? 0,
        formatter: (value: number) => value.toLocaleString('pt-BR'),
        gradient: 'linear-gradient(135deg, rgba(255,59,48,0.2) 0%, rgba(255,122,114,0.08) 100%)',
        accent: theme.colors.primary,
        hint: 'Volume total de oportunidades capturadas.',
      },
      {
        key: 'totalVendas',
        label: 'Total de Vendas',
        icon: Target,
        value: reportData?.estatisticas?.totalVendas ?? 0,
        formatter: (value: number) => value.toLocaleString('pt-BR'),
        gradient: 'linear-gradient(135deg, rgba(255,122,114,0.2) 0%, rgba(255,59,48,0.1) 100%)',
        accent: '#ff6b4a',
        hint: 'Negócios concluídos no período selecionado.',
      },
      {
        key: 'valorTotal',
        label: 'Valor Total',
        icon: DollarSign,
        value: reportData?.estatisticas?.valorTotal ?? 0,
        formatter: (value: number) => formatCurrency(value),
        gradient: 'linear-gradient(135deg, rgba(34,197,94,0.28) 0%, rgba(22,163,74,0.16) 100%)',
        accent: '#22C55E',
        hint: 'Receita consolidada em reais.',
      },
      {
        key: 'taxaConversao',
        label: 'Taxa de Conversão',
        icon: TrendingUp,
        value: reportData?.estatisticas?.taxaConversao ?? 0,
        formatter: (value: number) => formatPercent(value),
        gradient: 'linear-gradient(135deg, rgba(250, 160, 60, 0.28) 0%, rgba(255, 59, 48, 0.1) 100%)',
        accent: conversionTone,
        hint:
          conversionValue >= 25
            ? 'Conversões em ritmo excelente.'
            : conversionValue >= 10
            ? 'Conversões estáveis; explore melhorias.'
            : 'Conversões abaixo da média, recomendo ações imediatas.',
        tone: conversionTone,
      },
    ];

    if (reportData?.estatisticas?.ticketMedio !== undefined) {
      config.push({
        key: 'ticketMedio',
        label: 'Ticket Médio',
        icon: PieChart,
        value: reportData?.estatisticas?.ticketMedio ?? 0,
        formatter: (value: number) => formatCurrency(value),
        gradient: 'linear-gradient(135deg, rgba(255, 119, 71, 0.24) 0%, rgba(255, 202, 140, 0.12) 100%)',
        accent: '#f97316',
        hint: 'Valor médio por venda realizada.',
      });
    }

    return config;
  }, [reportData, theme.colors.success]);

  const additionalStats = useMemo(() => {
    if (!reportData?.estatisticas) return [];

    const ignoreKeys = new Set([
      'totalLeads',
      'totalVendas',
      'valorTotal',
      'taxaConversao',
      'ticketMedio',
      'leadsPorStatus',
    ]);

    return Object.entries(reportData.estatisticas)
      .filter(
        ([key, value]) =>
          !ignoreKeys.has(key) && typeof value === 'number' && Number.isFinite(Number(value))
      )
      .map(([key, value]) => {
        const numericValue = Number(value);
        const label = formatMetricLabel(key);
        const lowerKey = key.toLowerCase();
        let formatted: string;

        if (lowerKey.includes('valor') || lowerKey.includes('ticket')) {
          formatted = formatCurrency(numericValue);
        } else if (lowerKey.includes('taxa') || lowerKey.includes('percent')) {
          formatted = formatPercent(numericValue);
        } else {
          formatted = numericValue.toLocaleString('pt-BR');
        }

        return { key, label, formatted };
      });
  }, [reportData?.estatisticas]);

  const reportPeriod = useMemo(() => {
    if (!reportData?.periodoInicio && !reportData?.periodoFim) return null;

    const formatDate = (raw?: string) => {
      if (!raw) return null;
      const date = new Date(raw);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('pt-BR', { year: 'numeric', month: 'short', day: 'numeric' });
      }
      return raw;
    };

    const start = formatDate(reportData?.periodoInicio);
    const end = formatDate(reportData?.periodoFim);

    if (start && end) return `Período analisado: ${start} até ${end}`;
    if (start) return `Dados a partir de ${start}`;
    if (end) return `Dados até ${end}`;
    return null;
  }, [reportData?.periodoInicio, reportData?.periodoFim]);

  const topVendas = useMemo(() => {
    if (!reportData?.vendas || reportData.vendas.length === 0) return [];

    const sanitizeNumber = (value: any) => {
      if (typeof value === 'number') return value;
      if (!value) return null;
      const normalized = String(value).replace(/[^\d,-]/g, '').replace(',', '.');
      const parsed = Number.parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const formatDate = (raw: any) => {
      if (!raw) return '—';
      const date = new Date(raw);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('pt-BR');
      }
      return String(raw);
    };

    return reportData.vendas.slice(0, 3).map((venda) => {
      const valor = sanitizeNumber(venda?.valor ?? venda?.valorVenda);
      const cliente =
        venda?.cliente ?? venda?.nomeCliente ?? venda?.contato ?? 'Cliente não informado';
      const status = venda?.status ?? 'Sem status';
      const data = formatDate(venda?.dataVenda ?? venda?.dataCriacao ?? venda?.data);

      return {
        cliente: String(cliente),
        status: String(status),
        valor: valor !== null ? formatCurrency(valor) : '—',
        data,
      };
    });
  }, [reportData?.vendas]);

  const shouldRenderAnalysis = Boolean(
    reportPeriod ||
      additionalStats.length > 0 ||
      topVendas.length > 0 ||
      (loading && loadingState === 'generate')
  );

  const normalizedKeywords = useMemo(() => INSIGHT_KEYWORDS.map((item) => normalizeText(item)), []);

  const keywordRegex = useMemo(() => new RegExp(`(${INSIGHT_KEYWORDS.join('|')})`, 'gi'), []);

  const highlightText = useCallback(
    (text: string) => {
      const replaced = replaceMetricLabels(text);
      const parts = replaced.split(keywordRegex).filter((fragment) => fragment.length > 0);
      return parts.map((fragment, index) => {
        const normalized = normalizeText(fragment);
        return normalizedKeywords.includes(normalized) ? (
          <InsightKeyword key={`${fragment}-${index}`}>{fragment}</InsightKeyword>
        ) : (
          <span key={`${fragment}-${index}`}>{fragment}</span>
        );
      });
    },
    [keywordRegex, normalizedKeywords]
  );

  const insightsData = useMemo(() => {
    if (!reportData?.insights) {
      return { heading: '', sections: [] as InsightSection[] };
    }

    const lines = reportData.insights
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    let heading = '';
    const sections: InsightSection[] = [];
    let current: InsightSection | null = null;

    const ensureSection = () => {
      if (!current) {
        current = { title: 'Observações', items: [] };
        sections.push(current);
      }
      return current;
    };

    lines.forEach((line) => {
      if (!line) return;

      if (!line.startsWith('•') && !line.startsWith('-') && !line.endsWith(':') && line === line.toUpperCase()) {
        heading = replaceMetricLabels(line);
        return;
      }

      if (line.endsWith(':')) {
        const rawTitle = line.slice(0, -1).trim();
        const symbols = Array.from(rawTitle);
        const firstSymbol = symbols[0] ?? '';
        const restSymbols = symbols.slice(1).join('').trim();
        const isAlphaNumeric = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9]/.test(firstSymbol);

        const title = replaceMetricLabels((isAlphaNumeric ? rawTitle : restSymbols) || 'Seção');

        const section: InsightSection = { title, items: [] };
        sections.push(section);
        current = section;
        return;
      }

      const cleaned = replaceMetricLabels(line.replace(/^[-•]\s*/, '').trim());
      if (!cleaned) return;
      ensureSection().items.push(cleaned);
    });

    return { heading, sections };
  }, [reportData?.insights]);

  const insightsHeading = useMemo(() => {
    if (!insightsData.heading) return '';
    const normalized = insightsData.heading.toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }, [insightsData.heading]);

  return (
    <PageContainer>
      <PageHeader>
        <HeaderContent>
          <HeaderTitle>Relatórios</HeaderTitle>
          <HeaderSubtitle>
            Monitore vendas, leads e performance em uma visão única e personalizável, com filtros combináveis e visual consistente nos modos claro e escuro.
          </HeaderSubtitle>
        </HeaderContent>
      </PageHeader>

      <FiltersCard>
        <FiltersHeader>
          <div>
            <FiltersTitle>
              <Filter />
              Estratégia de filtros
            </FiltersTitle>
            <FiltersSubtitle>
              Combine períodos, tipos de relatório e inteligência artificial para análises sob medida.
            </FiltersSubtitle>
          </div>
          <BetaBadge>Nova experiência</BetaBadge>
        </FiltersHeader>

        <FiltersGrid>
          <FormGroup>
            <Label>
              <Gauge />
              Tipo de Relatório
            </Label>
            <Select
              value={filters.tipoRelatorio}
              onChange={(e) => handleFilterChange('tipoRelatorio', e.target.value)}
            >
              <option value="completo">Relatório Completo</option>
              <option value="vendas">Análise de Vendas</option>
              <option value="leads">Análise de Leads</option>
              <option value="performance">Performance</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>
              <CalendarDays />
              Data Início
            </Label>
            <Input
              type="date"
              value={filters.dataInicio}
              onChange={(e) => handleFilterChange('dataInicio', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <CalendarDays />
              Data Fim
            </Label>
            <Input
              type="date"
              value={filters.dataFim}
              onChange={(e) => handleFilterChange('dataFim', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>&nbsp;</Label>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="incluirInsights"
                checked={filters.incluirInsights}
                onChange={(e) => handleFilterChange('incluirInsights', e.target.checked)}
              />
              <label htmlFor="incluirInsights">Incluir Insights de IA</label>
            </CheckboxGroup>
          </FormGroup>
        </FiltersGrid>

        <FiltersDivider />

        <ButtonGroup>
          <Button onClick={generateReport} disabled={loading} $loading={loadingState === 'generate'}>
            {loadingState === 'generate' ? <LoadingSpinner /> : <Gauge />}
            {loadingState === 'generate' ? 'Gerando relatório...' : 'Gerar Relatório'}
          </Button>
          <DownloadButton
            $variant="secondary"
            onClick={downloadPDF}
            disabled={loading}
            $loading={loadingState === 'download'}
          >
            {loadingState === 'download' ? <LoadingSpinner /> : <Download />}
            {loadingState === 'download' ? 'Preparando PDF...' : 'Download PDF'}
          </DownloadButton>
        </ButtonGroup>

        {progress > 0 && progress < 100 && <ProgressBar $progress={progress} />}
      </FiltersCard>

      {error && (
        <ErrorMessage>
          <ErrorHeader>
            <AlertTriangle />
            {error}
          </ErrorHeader>
          <ErrorActions>
            <Button
              $variant="primary"
              onClick={() => {
                if (lastAction === 'generate') {
                  generateReport();
                } else {
                  loadSummary();
                }
              }}
            >
              Tentar novamente
            </Button>
            <Button $variant="ghost" onClick={() => setError(null)}>
              Dispensar alerta
            </Button>
          </ErrorActions>
        </ErrorMessage>
      )}

      {loading && loadingState === 'summary' && (
        <LoadingState>
          <LoadingSpinner />
          Buscando estatísticas atualizadas...
        </LoadingState>
      )}

      {(!loading || loadingState !== 'summary') && reportData && (
        <>
          <StatsSection>
            <StatsHeader>
              <BarChart3 />
              Destrave oportunidades com indicadores premium
            </StatsHeader>

            {loading && loadingState === 'generate' ? (
              <StatsSkeletonGrid>
                {Array.from({ length: 4 }).map((_, index) => (
                  <SkeletonCard key={`skeleton-${index}`} />
                ))}
              </StatsSkeletonGrid>
            ) : (
              <StatsGrid>
                {statsConfig.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <StatCard
                      key={stat.key}
                      $gradient={stat.gradient}
                      $accent={stat.accent}
                      $delay={index * 90}
                      title={stat.hint}
                    >
                      <StatIcon $accent={stat.accent}>
                        <Icon />
                      </StatIcon>
                      <StatLabel>{stat.label}</StatLabel>
                      <StatValue $tone={stat.tone}>{stat.formatter(stat.value ?? 0)}</StatValue>
                      <StatHint>{stat.hint}</StatHint>
                    </StatCard>
                  );
                })}
              </StatsGrid>
            )}
          </StatsSection>

          {shouldRenderAnalysis && (
            <AnalysisSection>
              <SectionHeader>
                <SectionTitle>Visão analítica</SectionTitle>
                <SectionSubtitle>
                  Entenda detalhes complementares sobre desempenho, status de leads e vendas recentes.
                </SectionSubtitle>
              </SectionHeader>
              {reportPeriod && <PeriodBadge>{reportPeriod}</PeriodBadge>}

              {loading && loadingState === 'generate' ? (
                <DetailedSkeletonGrid>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <SkeletonTile key={`analysis-skeleton-${index}`} />
                  ))}
                </DetailedSkeletonGrid>
              ) : (
                <>
                  {additionalStats.length > 0 && (
                    <AnalysisGrid>
                      {additionalStats.map(({ key, label, formatted }) => (
                        <AnalysisCard key={key}>
                          <AnalysisLabel>{label}</AnalysisLabel>
                          <AnalysisValue>{formatted}</AnalysisValue>
                        </AnalysisCard>
                      ))}
                    </AnalysisGrid>
                  )}

                  {/* Removido bloco de Leads por status conforme solicitado */}

                  {topVendas.length > 0 && (
                    <div>
                      <SubSectionTitle>Principais vendas recentes</SubSectionTitle>
                      <TopList>
                        {topVendas.map((item, index) => (
                          <TopListItem key={`${item.cliente}-${index}`}>
                            <TopItemHeader>
                              <span>{item.cliente}</span>
                              <span>{item.valor}</span>
                            </TopItemHeader>
                            <TopItemMeta>
                              <span>Status: {item.status}</span>
                              <span>Data: {item.data}</span>
                            </TopItemMeta>
                          </TopListItem>
                        ))}
                      </TopList>
                    </div>
                  )}
                </>
              )}
            </AnalysisSection>
          )}

          {filters.incluirInsights && (
            <InsightsCard>
              <InsightsHeader>
                <InsightsTitleGroup>
                  <InsightsTitleRow>
                    <InsightsIcon>
                      <BrainCircuit />
                    </InsightsIcon>
                    <InsightsTitle>Insights estratégicos</InsightsTitle>
                  </InsightsTitleRow>
                  {insightsHeading && <InsightsSubtitle>{insightsHeading}</InsightsSubtitle>}
                </InsightsTitleGroup>
                <InsightsBadge>Análise neural</InsightsBadge>
              </InsightsHeader>

              {loading && loadingState === 'generate' ? (
                <InsightsSkeletonGrid>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <SkeletonTile key={`insight-skeleton-${index}`} />
                  ))}
                </InsightsSkeletonGrid>
              ) : reportData.insights ? (
                <InsightsContent>
                      {insightsData.sections.length > 0 ? (
                        insightsData.sections.map((section, sectionIndex) => (
                          <InsightTile key={`${section.title}-${sectionIndex}`}>
                            <InsightHeading>
                              <span>{section.title}</span>
                            </InsightHeading>
                        <InsightList>
                          {section.items.map((item, itemIndex) => (
                            <InsightListItem key={`${section.title}-${itemIndex}`}>
                              {highlightText(item)}
                            </InsightListItem>
                          ))}
                        </InsightList>
                      </InsightTile>
                    ))
                  ) : (
                    <InsightTile>
                      <InsightHeading>
                        <span>Sem insights disponíveis</span>
                      </InsightHeading>
                      <InsightList>
                        <InsightListItem>
                          Nenhum insight foi retornado para os filtros selecionados.
                        </InsightListItem>
                      </InsightList>
                    </InsightTile>
                  )}
                </InsightsContent>
              ) : (
                <InsightsSkeletonGrid>
                  <SkeletonTile />
                </InsightsSkeletonGrid>
              )}
            </InsightsCard>
          )}
        </>
      )}

      {hasLoadedOnce && !loading && !reportData && (
        <EmptyState>
          <Inbox />
          <div>
            Nenhum dado encontrado para os filtros selecionados.
            <br />
            Ajuste o período ou gere um novo relatório para visualizar métricas.
          </div>
        </EmptyState>
      )}
    </PageContainer>
  );
};

export default Reports;
